import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API_BASE, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, Copy, Save } from "lucide-react";

const SECTIONS = [
  "Executive Summary",
  "Statement of Need",
  "Project Description",
  "Goals & Objectives",
  "Methodology",
  "Timeline",
  "Budget Justification",
  "Evaluation Plan",
  "Sustainability Plan",
  "Organization Capacity",
];

export default function AIWriter() {
  const [params] = useSearchParams();
  const preselectedGrant = params.get("grant") || "";

  const [grants, setGrants] = useState([]);
  const [grantId, setGrantId] = useState(preselectedGrant);
  const [section, setSection] = useState("Executive Summary");
  const [orgName, setOrgName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const outRef = useRef(null);

  useEffect(() => {
    api.get("/grants/saved/list").then((r) => setGrants(r.data.grants || []));
  }, []);

  useEffect(() => {
    if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
  }, [output]);

  const run = async () => {
    if (!prompt.trim()) { toast.error("Add some guidance for the AI first"); return; }
    setOutput("");
    setStreaming(true);
    const token = localStorage.getItem("gf_token");
    try {
      const res = await fetch(`${API_BASE}/ai/write`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          grant_id: grantId || null,
          section,
          prompt,
          organization_name: orgName,
          project_name: projectName,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const chunk = line.slice(6);
          if (chunk === "[DONE]") { setStreaming(false); return; }
          if (chunk.startsWith("[ERROR]")) { toast.error(chunk); setStreaming(false); return; }
          setOutput((prev) => prev + chunk);
        }
      }
    } catch (err) {
      toast.error("AI writer failed. Try again.");
    } finally {
      setStreaming(false);
    }
  };

  const copyOutput = async () => {
    await navigator.clipboard.writeText(output);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="space-y-8" data-testid="ai-writer-page">
      <header>
        <div className="label-eyebrow mb-2">AI grant writer</div>
        <h1 className="font-heading font-light text-4xl md:text-5xl tracking-tighter">
          <span className="italic text-[#1C3F35]">Write</span> to the RFP.
        </h1>
        <p className="text-[#5E5E5E] mt-2">Claude Sonnet 4.5 drafts your section. You keep the voice — and the credit.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E6E4DF] rounded-lg p-6 space-y-5" data-testid="ai-form">
          <div>
            <Label>Section</Label>
            <Select value={section} onValueChange={setSection}>
              <SelectTrigger data-testid="ai-section" className="mt-2 bg-white border-[#E6E4DF]"><SelectValue/></SelectTrigger>
              <SelectContent>
                {SECTIONS.map((s)=> <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Grant (optional)</Label>
            <Select value={grantId || "none"} onValueChange={(v)=>setGrantId(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="ai-grant" className="mt-2 bg-white border-[#E6E4DF]">
                <SelectValue placeholder="Select a saved grant"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {grants.map((g)=> <SelectItem key={g.id} value={g.id}>{g.title.slice(0,60)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Organization</Label>
              <Input
                value={orgName} onChange={(e)=>setOrgName(e.target.value)}
                data-testid="ai-org" className="mt-2 bg-white border-[#E6E4DF]"
                placeholder="Bridge Community Trust"
              />
            </div>
            <div>
              <Label>Project</Label>
              <Input
                value={projectName} onChange={(e)=>setProjectName(e.target.value)}
                data-testid="ai-project" className="mt-2 bg-white border-[#E6E4DF]"
                placeholder="Youth Mentorship 2026"
              />
            </div>
          </div>

          <div>
            <Label>What to include</Label>
            <Textarea
              value={prompt} onChange={(e)=>setPrompt(e.target.value)}
              data-testid="ai-prompt"
              className="mt-2 bg-white border-[#E6E4DF]"
              rows={6}
              placeholder="e.g. We serve at-risk youth in Detroit through mentoring and after-school programs. Emphasize measurable outcomes and community partnerships."
            />
          </div>

          <Button
            onClick={run} disabled={streaming}
            data-testid="ai-generate"
            className="w-full bg-[#1C3F35] text-white hover:bg-[#1A362D] py-6"
          >
            {streaming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/> Drafting…</> : <><Sparkles className="w-4 h-4 mr-2"/>Generate section</>}
          </Button>
        </div>

        <div className="bg-white border border-[#E6E4DF] rounded-lg flex flex-col min-h-[520px]" data-testid="ai-output-panel">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E4DF]">
            <div className="font-heading font-medium">{section}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={copyOutput} disabled={!output} data-testid="ai-copy">
                <Copy className="w-3.5 h-3.5 mr-1"/>Copy
              </Button>
            </div>
          </div>
          <div ref={outRef} className="flex-1 overflow-y-auto p-6 whitespace-pre-wrap text-[#1A1A1A] leading-relaxed" data-testid="ai-output">
            {output || <div className="text-[#5E5E5E] italic">Your generated section will stream here…</div>}
            {streaming && <span className="inline-block w-2 h-4 bg-[#1C3F35] align-middle animate-pulse ml-1"/>}
          </div>
        </div>
      </div>
    </div>
  );
}
